import { useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { AuthContextProvider, useAuthContext } from './context/AuthContext'
import Login from './screens/Login'

// ── Page imports ──────────────────────────────────────────────────────────────
import Dashboard     from './pages/Dashboard'
import Analytics     from './pages/Analytics'
import Users         from './pages/Users'
import Sellers       from './pages/Sellers'
import Creators       from './pages/Creators'
import Drivers       from './pages/Drivers'
import Reels         from './pages/Reels'
import LiveStreams   from './pages/LiveStreams'
import Comments      from './pages/Comments'
import Flagged       from './pages/Flagged'
import VideoModeration from './pages/VideoModeration'
import Payments      from './pages/Payments'
import DriverPayment  from './pages/DriverPayment'
import SellerPayment  from './pages/SellerPayment'
import PayoutBatch    from './pages/PayoutBatch'
import Orders        from './pages/Orders'
import HelpCenter    from './pages/HelpCenter'
import Terms         from './pages/Terms'
import Notifications from './pages/Notifications'
import Roles              from './pages/Roles'
import Settings           from './pages/Settings'
import SellerTiers        from './pages/SellerTiers'
import CreatorPayout      from './pages/CreatorPayout'
import CreatorPayoutBatch from './pages/CreatorPayoutBatch'
import CreatorTiers       from './pages/CreatorTiers'
import DisputeResolution  from './pages/DisputeResolution'
import HomePage, { LegalPage } from './screens/HomePage'
import Ads                from './pages/Ads'

// ── Nav structure (single source of truth for sidebar + routes) ───────────────
const NAV = [
  {
    group: 'Overview',
    items: [
      { id: 'dashboard',     label: 'Dashboard',           icon: '⊞',  path: '/dashboard'     },
      { id: 'analytics',     label: 'Analytics',           icon: '📊', path: '/analytics'     },
    ],
  },
  {
    group: 'People',
    items: [
      { id: 'users',         label: 'User Management',     icon: '👤', path: '/users'         },
      { id: 'sellers',       label: 'Shop & Sellers',      icon: '🏪', path: '/sellers'       },
      { id: 'creators',      label: 'Creators',            icon: '🎥', path: '/creators'      },
      { id: 'drivers',       label: 'Drivers & Companies', icon: '🚚', path: '/drivers'       },
    ],
  },
  {
    group: 'Content',
    items: [
      { id: 'reels',         label: 'Clips Management',    icon: '▶',  path: '/reels'         },
      { id: 'livestreams',   label: 'Live Streams',        icon: '🔴', path: '/livestreams'   },
      { id: 'comments',      label: 'Comments & Ratings',  icon: '💬', path: '/comments',    },
      { id: 'flagged',       label: 'Flagged & Reported',  icon: '⚑',  path: '/flagged'       },
      { id: 'videos',        label: 'Video Moderation',    icon: '🎬', path: '/videos'        },
    ],
  },
  {
    group: 'Commerce',
    items: [
      { id: 'payments',       label: 'Payment Reports',     icon: '💳', path: '/payments'        },
      { id: 'driver-payment',  label: 'Driver Payments',    icon: '🚗', path: '/driver-payments'  },
      { id: 'seller-payment', label: 'Seller Payouts',     icon: '🏪', path: '/seller-payouts'  },
      { id: 'payout-batch',   label: 'Payout Batches',     icon: '💳', path: '/payout-batches'  },
      { id: 'creator-payout', label: 'Creator Payouts',    icon: '🎥', path: '/creator-payouts' },
      { id: 'creator-payout-batch', label: 'Creator Payout Batches', icon: '💳', path: '/creator-payout-batches' },
      { id: 'orders',         label: 'Orders',             icon: '📦', path: '/orders'          },
      { id: 'seller-tiers',   label: 'Seller Tiers',       icon: '🏆', path: '/seller-tiers'    },
      { id: 'creator-tiers',  label: 'Creator Tiers',      icon: '📺', path: '/creator-tiers'   },
      { id: 'disputes',       label: 'Dispute Resolution', icon: '⚖️',  path: '/disputes'        },
    ],
  },
  {
    group: 'Advertising',
    items: [
      { id: 'ads', label: 'Ads', icon: '📣', path: '/ads' },
    ],
  },
  {
    group: 'Support & Legal',
    items: [
      { id: 'helpcenter',    label: 'Help Center',         icon: '🎧', path: '/helpcenter',  },
      { id: 'terms',         label: 'Terms & Privacy',     icon: '📄', path: '/terms'         },
    ],
  },
  {
    group: 'System',
    items: [
      { id: 'notifications', label: 'Notifications',       icon: '🔔', path: '/notifications' },
      { id: 'roles',         label: 'Roles & Permissions', icon: '🔑', path: '/roles'         },
      { id: 'settings',      label: 'Settings',            icon: '⚙️',  path: '/settings'      },
    ],
  },
]

// Flat list of all items for easy lookup
const ALL_ITEMS = NAV.flatMap(g => g.items)

// ── Sidebar color tokens (matches Login page navy theme) ──────────────────────
const SB = {
  bg:       '#060D1F',   // same as Login C.navy
  bgMid:    '#0D1835',   // same as Login C.navyMid
  line:     '#111D36',   // same as Login C.navyLine
  orange:   '#E8440A',   // same as Login C.orange
  activeHl: 'rgba(232,68,10,0.14)',
  hovBg:    'rgba(255,255,255,0.05)',
  textDim:  'rgba(255,255,255,0.35)',
  textMid:  'rgba(255,255,255,0.55)',
  textOn:   '#FFFFFF',
}

// ── Nav item ──────────────────────────────────────────────────────────────────
function NavItem({ item, active }) {
  const navigate = useNavigate()
  const [hov, setHov] = useState(false)

  return (
    <div
      onClick={() => navigate(item.path)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 20px', cursor: 'pointer',
        fontSize: 13, fontWeight: active ? 600 : 500,
        color: active ? SB.textOn : hov ? 'rgba(255,255,255,0.80)' : SB.textMid,
        background: active ? SB.activeHl : hov ? SB.hovBg : 'transparent',
        borderLeft: `2px solid ${active ? SB.orange : 'transparent'}`,
        transition: 'all 0.14s', userSelect: 'none',
      }}
    >
      <span style={{ fontSize: 14, width: 20, opacity: active ? 1 : 0.75 }}>{item.icon}</span>
      <span style={{ flex: 1 }}>{item.label}</span>
      {item.badge && (
        <span style={{
          background: SB.orange, color: '#fff', fontSize: 9, fontWeight: 700,
          fontFamily: "'JetBrains Mono', monospace",
          padding: '2px 6px', borderRadius: 3, lineHeight: 1.4,
        }}>{item.badge}</span>
      )}
    </div>
  )
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ authUser, onLogout }) {
  const location = useLocation()
  const activePath = location.pathname

  return (
    <aside style={{
      width: 248, flexShrink: 0,
      background: SB.bg,
      display: 'flex', flexDirection: 'column',
      position: 'fixed', top: 0, left: 0, bottom: 0,
      overflowY: 'auto', zIndex: 100,
      fontFamily: "'Inter', 'Plus Jakarta Sans', sans-serif",
    }}>
      {/* Logo */}
      <div style={{
        padding: '20px 20px 16px',
        borderBottom: `1px solid ${SB.line}`,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <img src="/kudos_logo.png" alt="Kudos Live" style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0, objectFit: 'cover',
          boxShadow: '0 4px 14px rgba(232,68,10,0.30)',
        }} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
        <div style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0, position: 'relative', overflow: 'hidden',
          background: `linear-gradient(145deg, ${SB.orange} 0%, #c9380a 100%)`,
          display: 'none', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(232,68,10,0.30)',
        }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'rgba(255,255,255,0.12)', pointerEvents: 'none' }} />
          <span style={{ fontSize: 16, fontWeight: 900, color: '#fff', letterSpacing: '-0.04em' }}>K</span>
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: SB.textOn, letterSpacing: '-0.03em', lineHeight: 1 }}>Kudos Live</div>
          <div style={{ fontSize: 10, color: SB.textDim, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, fontWeight: 500 }}>
            Admin Dashboard
          </div>
        </div>
      </div>

      {/* Nav groups */}
      <nav className="sidebar-nav" style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
        {NAV.map(({ group, items }) => (
          <div key={group} style={{ marginBottom: 2 }}>
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: SB.textDim, padding: '12px 20px 4px',
              fontFamily: "'JetBrains Mono', monospace",
            }}>{group}</div>
            {items.map(item => (
              <NavItem
                key={item.id}
                item={item}
                active={activePath === item.path || activePath.startsWith(item.path + '/')}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* Footer — user + logout */}
      <div style={{
        padding: '12px 16px', borderTop: `1px solid ${SB.line}`,
        display: 'flex', alignItems: 'center', gap: 10,
        background: SB.bgMid,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, flexShrink: 0, overflow: 'hidden', position: 'relative',
          background: `linear-gradient(145deg, ${SB.orange} 0%, #c9380a 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(232,68,10,0.25)',
        }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>
            {authUser?.username?.charAt(0).toUpperCase() || 'A'}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: SB.textOn, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {authUser?.username || 'Admin'}
          </div>
          <div style={{ fontSize: 10, color: SB.textDim, fontFamily: "'JetBrains Mono', monospace", marginTop: 1 }}>
            {authUser?.role || 'admin'}
          </div>
        </div>
        <div
          onClick={onLogout}
          title="Log out"
          style={{ color: SB.textDim, cursor: 'pointer', fontSize: 15, padding: 4, borderRadius: 6, transition: 'color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color = SB.textOn}
          onMouseLeave={e => e.currentTarget.style.color = SB.textDim}
        >↩</div>
      </div>
    </aside>
  )
}

// ── Topbar ────────────────────────────────────────────────────────────────────
function Topbar() {
  const location = useLocation()
  const current  = ALL_ITEMS.find(i => location.pathname === i.path || location.pathname.startsWith(i.path + '/'))
  const label    = current?.label || 'Dashboard'

  return (
    <div style={{
      background: '#fff', borderBottom: '1px solid #e9e9e4',
      padding: '0 32px', height: 54,
      display: 'flex', alignItems: 'center', gap: 10,
      position: 'sticky', top: 0, zIndex: 50,
    }}>
      <span style={{ fontSize: 12, color: '#9a9a94' }}>Admin</span>
      <span style={{ color: '#e9e9e4' }}>/</span>
      <span style={{ fontSize: 14, fontWeight: 700 }}>{label}</span>
      <div style={{ flex: 1 }} />
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: '#f6f6f3', border: '1px solid #e9e9e4',
        padding: '7px 13px', borderRadius: 7, fontSize: 12, color: '#9a9a94',
      }}>🔍&nbsp; Search anything…</div>
      <div style={{
        width: 34, height: 34, borderRadius: 7,
        background: '#f6f6f3', border: '1px solid #e9e9e4',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, cursor: 'pointer', position: 'relative',
      }}>
        🔔
        <span style={{
          position: 'absolute', top: 7, right: 7,
          width: 7, height: 7, borderRadius: '50%',
          background: '#e8440a', border: '1.5px solid #fff',
        }} />
      </div>
    </div>
  )
}

// ── Loading splash ────────────────────────────────────────────────────────────
function LoadingSplash() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f6f6f3', fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
      <div style={{ textAlign: 'center' }}>
        <img src="/kudos_logo.png" alt="Kudos Live" style={{
          width: 38, height: 38, borderRadius: 10, objectFit: 'cover', margin: '0 auto 14px', display: 'block',
        }} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
        <div style={{
          width: 38, height: 38, borderRadius: 10, background: '#e8440a',
          display: 'none', alignItems: 'center', justifyContent: 'center',
          fontSize: 19, fontWeight: 900, color: '#fff', margin: '0 auto 14px',
        }}>K</div>
        <div style={{ fontSize: 13, color: '#9a9a94' }}>Loading…</div>
      </div>
    </div>
  )
}

// ── Route guards ──────────────────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const { authUser, isLoading } = useAuthContext()
  if (isLoading) return <LoadingSplash />
  if (!authUser) return <Navigate to="/login" replace />
  return children
}

function PublicRoute({ children }) {
  const { authUser, isLoading } = useAuthContext()
  if (isLoading) return <LoadingSplash />
  if (authUser)  return <Navigate to="/dashboard" replace />
  return children
}

// ── Admin shell (wraps all protected pages) ───────────────────────────────────
function AdminShell({ children }) {
  const { authUser, logout } = useAuthContext()

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", background: '#f6f6f3' }}>
      <Sidebar authUser={authUser} onLogout={logout} />
      <div style={{ marginLeft: 248, flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Topbar />
        <div style={{ flex: 1 }}>
          {children}
        </div>
        <div style={{ padding: '14px 24px', textAlign: 'center', fontSize: 11, color: '#9a9a94' }}>
          © {new Date().getFullYear()} Honor Creations Ltd. All rights reserved.
        </div>
      </div>
    </div>
  )
}

// ── AppRouter ─────────────────────────────────────────────────────────────────
function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* ── Public — always accessible, no auth redirect (Google Play Console links here) ── */}
      <Route path="/privacy" element={<LegalPage type="privacy" />} />
      <Route path="/terms"   element={<LegalPage type="terms" />} />

      {/* ── Public ── */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
     

      {/* ── Protected ── */}
      <Route
        path="/dashboard"
        element={<ProtectedRoute><AdminShell><Dashboard /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/analytics"
        element={<ProtectedRoute><AdminShell><Analytics /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/users"
        element={<ProtectedRoute><AdminShell><Users /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/sellers"
        element={<ProtectedRoute><AdminShell><Sellers /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/creators"
        element={<ProtectedRoute><AdminShell><Creators /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/drivers"
        element={<ProtectedRoute><AdminShell><Drivers /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/reels"
        element={<ProtectedRoute><AdminShell><Reels /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/livestreams"
        element={<ProtectedRoute><AdminShell><LiveStreams /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/comments"
        element={<ProtectedRoute><AdminShell><Comments /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/flagged"
        element={<ProtectedRoute><AdminShell><Flagged /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/videos"
        element={<ProtectedRoute><AdminShell><VideoModeration /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/payments"
        element={<ProtectedRoute><AdminShell><Payments /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/driver-payments"
        element={<ProtectedRoute><AdminShell><DriverPayment /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/seller-payouts"
        element={<ProtectedRoute><AdminShell><SellerPayment /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/payout-batches"
        element={<ProtectedRoute><AdminShell><PayoutBatch /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/creator-payouts"
        element={<ProtectedRoute><AdminShell><CreatorPayout /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/creator-payout-batches"
        element={<ProtectedRoute><AdminShell><CreatorPayoutBatch /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/orders"
        element={<ProtectedRoute><AdminShell><Orders /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/seller-tiers"
        element={<ProtectedRoute><AdminShell><SellerTiers /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/creator-tiers"
        element={<ProtectedRoute><AdminShell><CreatorTiers /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/disputes"
        element={<ProtectedRoute><AdminShell><DisputeResolution /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/ads"
        element={<ProtectedRoute><AdminShell><Ads /></AdminShell></ProtectedRoute>}
      />

      <Route
        path="/helpcenter"
        element={<ProtectedRoute><AdminShell><HelpCenter /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/terms"
        element={<ProtectedRoute><AdminShell><Terms /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/notifications"
        element={<ProtectedRoute><AdminShell><Notifications /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/roles"
        element={<ProtectedRoute><AdminShell><Roles /></AdminShell></ProtectedRoute>}
      />
      <Route
        path="/settings"
        element={<ProtectedRoute><AdminShell><Settings /></AdminShell></ProtectedRoute>}
      />

      {/* ── Catch-all ── */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />

    </Routes>
  )
}

// ── App root ──────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthContextProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AuthContextProvider>
  )
}